import { useEffect, useRef } from 'react';
import { APIProvider, AdvancedMarker, Map, useMap } from '@vis.gl/react-google-maps';
import type { LiveStation, Semaforo } from '@/api/painel';
import { FALLBACK_CENTER, FALLBACK_ZOOM, MAP_ID } from '@/components/map/mapConstants';
import { useAppConfig } from '@/lib/config';

const PAINEL_MAP_ID = 'bow-painel-map';

const COR: Record<Semaforo, string> = { verde: '#00C072', amarelo: '#FFB92A', vermelho: '#EE263E' };
const ICONE: Record<Semaforo, string> = { verde: '●', amarelo: '▲', vermelho: '■' };

/** O pior estado da estação manda no pino (um carregador fora já pede atenção). */
function piorSemaforo(st: LiveStation): Semaforo {
  const s = st.carregadores.map((c) => c.semaforo);
  return s.includes('vermelho') ? 'vermelho' : s.includes('amarelo') ? 'amarelo' : 'verde';
}

function comCoordenada(estacoes: LiveStation[]) {
  return estacoes.filter(
    (s): s is LiveStation & { latitude: number; longitude: number } =>
      s.latitude != null && s.longitude != null && Number.isFinite(s.latitude) && Number.isFinite(s.longitude)
  );
}

/** Enquadra as estações uma vez (o polling de 30 s não recentraliza o mapa). */
function FitOnce({ estacoes }: { estacoes: LiveStation[] }) {
  const map = useMap(PAINEL_MAP_ID);
  const done = useRef(false);
  useEffect(() => {
    const pts = comCoordenada(estacoes);
    if (!map || done.current || pts.length === 0) return;
    done.current = true;
    if (pts.length === 1) {
      map.setCenter({ lat: pts[0]!.latitude, lng: pts[0]!.longitude });
      map.setZoom(15);
      return;
    }
    const b = new google.maps.LatLngBounds();
    for (const p of pts) b.extend({ lat: p.latitude, lng: p.longitude });
    map.fitBounds(b, 48);
  }, [map, estacoes]);
  return null;
}

/** Mapa da rede (só `network_view`, como na Visão geral da Tupi). Sem chave do Maps, some. */
export function PainelMapa({ estacoes }: { estacoes: LiveStation[] }) {
  const { google_maps_api_key } = useAppConfig();
  const pts = comCoordenada(estacoes);
  if (!google_maps_api_key || pts.length === 0) return null;
  return (
    <APIProvider apiKey={google_maps_api_key}>
      <div className="h-[280px] overflow-hidden rounded-3xl shadow-soft lg:h-[360px]">
        <Map
          id={PAINEL_MAP_ID}
          mapId={MAP_ID}
          defaultCenter={FALLBACK_CENTER}
          defaultZoom={FALLBACK_ZOOM}
          disableDefaultUI
          gestureHandling="cooperative"
          style={{ width: '100%', height: '100%' }}
        >
          <FitOnce estacoes={estacoes} />
          {pts.map((st) => {
            const s = piorSemaforo(st);
            return (
              <AdvancedMarker key={st.id} position={{ lat: st.latitude, lng: st.longitude }} title={st.nome}>
                <span className="inline-flex items-center gap-1 rounded-full bg-white px-2.5 py-1 text-[12px] font-semibold text-bow-royal shadow-mid">
                  <span aria-hidden style={{ color: COR[s], fontSize: 10 }}>
                    {ICONE[s]}
                  </span>
                  {st.nome}
                </span>
              </AdvancedMarker>
            );
          })}
        </Map>
      </div>
    </APIProvider>
  );
}
